import { useState, useEffect } from "react";
import { useCookies } from "react-cookie";

export default function Table() {
    const [events, setEvents] = useState([]);
    const [allEvents, setAllEvents] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [cookies] = useCookies(["token"]);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const headers = { Authorization: `Bearer ${cookies.token}` };

                const [notifRes, torrentsRes, requestsRes, seasonsRes] = await Promise.all([
                    fetch("https://api.industriegaming.fr/api/auth/notification", { headers }).catch(() => null),
                    fetch("https://api.industriegaming.fr/api/script/torrents/getall", { headers }).catch(() => null),
                    fetch("https://api.industriegaming.fr/api/script/request/getall", { headers }).catch(() => null),
                    fetch("https://api.industriegaming.fr/api/garden/seasons", { headers }).catch(() => null),
                ]);

                const [notifs, torrents, requests, seasons] = await Promise.all([
                    notifRes && notifRes.ok ? notifRes.json() : [],
                    torrentsRes && torrentsRes.ok ? torrentsRes.json() : [],
                    requestsRes && requestsRes.ok ? requestsRes.json() : [],
                    seasonsRes && seasonsRes.ok ? seasonsRes.json() : [],
                ]);

                // If seasons include GardenSemis with only ids, fetch full season details per season
                const enrichedSeasons = await Promise.all((seasons || []).map(async (season) => {
                    const firstSemis = season.GardenSemis && season.GardenSemis[0];
                    if (firstSemis && firstSemis.plant === undefined) {
                        try {
                            const res = await fetch(`https://api.industriegaming.fr/api/garden/season/${season.id}`, { headers });
                            if (res.ok) {
                                const full = await res.json();
                                return full;
                            }
                        } catch (err) {
                            // ignore and fallback to original season
                        }
                    }
                    return season;
                }));

                const evts = [];

                // Notifications (may not have a date)
                // (notifs || []).forEach(n => {
                //     evts.push({
                //         id: `notif-${n.id}`,
                //         actor: n.type || "Notification",
                //         action: n.text,
                //         detail: "",
                //         status: n.type,
                //         date: null
                //     });
                // });

                // Torrents
                // Torrents (only those requested)
                (torrents || [])
                    .filter(t => t && (t.requested === true || t.requested === 1 || String(t.requested) === '1'))
                    .forEach(t => {
                        evts.push({
                            id: `torrent-${t.id}`,
                            actor: t.web || t.title || "Torrent",
                            action: t.title || "Torrent demandé",
                            detail: t.lien || "",
                            status: "Demandé",
                            statusType: "torrent-requested",
                            date: t.created_at || null
                        });
                    });

                // Requests
                const formatUser = (value) => {
                    if (value === "3") return "Tout le monde";
                    if (value === "195249015858790400") return "Phil";
                    if (value === "723421020471427122") return "Strass";
                    return value;
                };

                (requests || []).forEach(r => {
                    evts.push({
                        id: `request-${r.id}`,
                        actor: formatUser(r.user),
                        action: r.title || "Nouvelle requête",
                        detail: "",
                        status: "Request",
                        date: r.created_at || null
                    });
                });

                // Garden semis from seasons
                (enrichedSeasons || []).forEach(season => {
                    (season.GardenSemis || []).forEach(s => {
                        evts.push({
                            id: `semis-${s.id}`,
                            actor: season.name || `Saison ${season.year}`,
                            action: `Semis: ${s.plant || `#${s.id}`}`,
                            detail: s.notes || "",
                            status: s.cancelled ? "Annulé" : "Semis",
                            date: s.dateSemis || null
                        });
                    });
                });

                // Normalize dates and sort (most recent first). Items without date go last.
                const normalized = evts.map(e => ({ ...e, dateObj: e.date ? new Date(e.date) : null }));
                normalized.sort((a, b) => {
                    if (a.dateObj && b.dateObj) return b.dateObj.getTime() - a.dateObj.getTime();
                    if (a.dateObj) return -1; // a has date, b doesn't -> a first
                    if (b.dateObj) return 1;  // b has date, a doesn't -> b first
                    return 0; // neither has date
                });

                setAllEvents(normalized);
                setEvents(normalized.slice(0, 4)); // keep recent 4
            } catch (err) {
                console.error("Erreur lors du chargement de l'activité :", err);
            }
        };

        fetchEvents();
    }, [cookies.token]);

    const formatDate = (d) => {
        if (!d) return "—";
        try {
            return new Date(d).toLocaleString("fr-FR", { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
        } catch {
            return "—";
        }
    };

    const truncateText = (text, max = 60) => {
        if (!text) return "—";
        if (typeof text !== 'string') text = String(text);
        if (text.length <= max) return text;
        return text.slice(0, max - 1) + "…";
    };

    const statusClass = (s) => {
        if (!s) return "status-info";
        if (s === "error") return "status-cancelled";
        if (s === "warning") return "status-pending";
        if (s === "new") return "status-success";
        if (s === "torrent" || s === "torrent-requested" || s === "Request" || s === "Semis") return "status-success";
        if (s === "cancelled" || s === "Annulé" ) return "status-cancelled";
        return "status-info";
    };

    const toggleShow = () => {
        if (!showAll) {
            setEvents(allEvents.slice(0, 10));
        } else {
            setEvents(allEvents.slice(0, 4));
        }
        setShowAll(prev => !prev);
    };

    return (
        <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="card-title">Activité récente</span>
                <button onClick={toggleShow} className="refresh-btn" title={showAll ? 'Réduire la liste' : 'Afficher les 10 dernières activités'}>
                    {showAll ? 'Réduire la liste' : 'Afficher les 10 dernières activités'}
                </button>
            </div>

            <table className="table">
                <thead>
                    <tr>
                        <th>Client</th>
                        <th>Événement</th>
                        <th>Détail</th>
                        <th>Statut</th>
                    </tr>
                </thead>

                <tbody>
                    {events.length === 0 && (
                        <tr>
                            <td colSpan={4} style={{ textAlign: 'center' }}>Aucune activité récente</td>
                        </tr>
                    )}

                    {events.map(evt => (
                        <tr key={evt.id}>
                            <td>{evt.actor}</td>
                            <td>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div className="cell-with-tooltip" tabIndex={0}>
                                        <strong>{truncateText(evt.action, 15)}</strong>
                                        <div className="tooltip">{evt.action || ''}</div>
                                    </div>
                                    <small style={{ color: '#777' }}>{formatDate(evt.date)}</small>
                                </div>
                            </td>
                            <td>
                                <div className="cell-with-tooltip" tabIndex={0}>
                                    <span>{evt.detail ? truncateText(evt.detail, 60) : '—'}</span>
                                    {evt.detail ? <div className="tooltip">{evt.detail}</div> : null}
                                </div>
                            </td>
                            <td><span className={`status ${statusClass(evt.statusType || evt.status)}`}>{evt.status || 'info'}</span></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
